import React, { useCallback, useState } from "react";
import { View, ScrollView, Pressable, StyleSheet } from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import DsHeader from "@components/ds/DsHeader";
import { C, RText, Tag, ChevronRight } from "@components/ds/theme";
import { t } from "@constants/localization";
import { getReceivingHub } from "@services/api/receiving";
import { showReceivingError } from "../showError";
import { BUCKET, BUCKET_META, shortDate } from "../helpers";

const PATH = "Receiving/hub";
// Today, then Pending (overdue, clear first), then Advance (product order).
const ORDER = [BUCKET.TODAY, BUCKET.PENDING, BUCKET.ADVANCE];

// Per-bucket colours from the mockup (§9 frame 01): Today is the red-outlined
// primary card with a red CTA; Advance and Pending use green CTAs and amber
// "to rack" counts.
const LOOK = {
  [BUCKET.TODAY]: {
    pillBg: C.chipRedBg,
    pillInk: C.chipRed,
    toRackInk: C.red,
    cta: C.red,
    primary: true,
  },
  [BUCKET.ADVANCE]: {
    pillBg: C.pendingBg,
    pillInk: C.pending,
    toRackInk: C.pending,
    cta: C.green,
  },
  [BUCKET.PENDING]: {
    pillBg: C.redBg,
    pillInk: C.red,
    toRackInk: C.pending,
    cta: C.green,
  },
};

const LEGEND = [
  { key: "Today", color: C.green },
  { key: "Pending", color: C.red },
  { key: "Advance", color: C.pending },
];

const percentOf = (racked, total) =>
  total > 0 ? `${Math.round((racked / total) * 100)}%` : "0%";

// The racking hub, pixel-matched to the mockup (§9 frame 01): a legend card,
// then Today, Advance and Pending, each opening ReceivingBucketList.
// Registered as `ReceivingTpList`, the home widget's `nav_to`, so the
// Receiving & Racks tile lands here on this build.
const ReceivingHub = ({ navigation }) => {
  const [hub, setHub] = useState(null);

  useFocusEffect(
    useCallback(() => {
      let active = true;
      getReceivingHub()
        .then(({ data }) => active && setHub(data?.data || null))
        .catch((error) => showReceivingError(error));
      return () => {
        active = false;
      };
    }, []),
  );

  const renderCard = (bucket) => {
    const meta = BUCKET_META[bucket];
    const look = LOOK[bucket];
    const counts = hub?.[meta.key] || {};
    const racked = Number(counts.racked ?? 0);
    const total = Number(counts.total ?? 0);
    const sub =
      bucket === BUCKET.TODAY
        ? t(`${PATH}/todaySub`, { date: shortDate(counts.date) })
        : t(`${PATH}/${meta.key}Sub`);
    const fill = { width: percentOf(racked, total) };

    return (
      <Pressable
        key={bucket}
        testID={`receiving-hub-${bucket}`}
        style={[styles.card, look.primary && styles.cardPrimary]}
        onPress={() =>
          navigation.navigate("ReceivingBucketList", {
            bucket,
            date: hub?.today?.date,
          })
        }
      >
        <View style={styles.top}>
          <RText size={17} weight={700} style={styles.title}>
            {t(`${PATH}/${meta.key}Title`)}
          </RText>
          <Tag
            label={bucket}
            bg={look.pillBg}
            color={look.pillInk}
            weight={700}
            style={styles.pill}
          />
        </View>
        <RText size={12.5} weight={500} color={C.muted} style={styles.sub}>
          {sub}
        </RText>
        <View style={styles.track}>
          <View style={[styles.fill, fill]} />
        </View>
        <View style={styles.bottom}>
          <RText size={13} weight={600} style={styles.grow}>
            <RText size={13} weight={600} color={look.toRackInk}>
              {t(`${PATH}/toRack`, { count: String(counts.to_rack ?? 0) })}
            </RText>
            <RText size={13} weight={500} color={C.muted}>
              {t(`${PATH}/racked`, {
                racked: String(racked),
                total: String(total),
              })}
            </RText>
          </RText>
          <View style={[styles.cta, { backgroundColor: look.cta }]}>
            <RText size={14} weight={600} color={C.surface}>
              {t(`${PATH}/cta`)}
            </RText>
            <ChevronRight color={C.surface} />
          </View>
        </View>
      </Pressable>
    );
  };

  return (
    <View style={styles.screen}>
      <DsHeader
        title={t(`${PATH}/title`)}
        navigation={navigation}
        testID="receiving-hub-back"
      />
      <ScrollView contentContainerStyle={styles.body}>
        <View style={styles.legend}>
          {LEGEND.map(({ key, color }) => (
            <View key={key} style={styles.legendRow}>
              <RText
                size={12}
                weight={700}
                color={color}
                style={styles.legendKey}
              >
                {t(`${PATH}/legend${key}`)}
              </RText>
              <RText size={12} weight={500} color={C.muted} style={styles.grow}>
                {t(`${PATH}/legend${key}Text`)}
              </RText>
            </View>
          ))}
        </View>
        {ORDER.map(renderCard)}
      </ScrollView>
    </View>
  );
};

export default ReceivingHub;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  body: { paddingTop: 14, paddingHorizontal: 16, paddingBottom: 24, gap: 12 },
  legend: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
    gap: 7,
  },
  legendRow: { flexDirection: "row", gap: 8 },
  legendKey: { flexShrink: 0, lineHeight: 17 },
  grow: { flex: 1, lineHeight: 17 },
  card: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 14,
    padding: 15,
    gap: 11,
  },
  cardPrimary: { borderWidth: 2, borderColor: C.red },
  top: { flexDirection: "row", alignItems: "center", gap: 8 },
  title: { flex: 1, letterSpacing: -0.2 },
  pill: { paddingHorizontal: 10 },
  sub: { marginTop: -5 },
  track: {
    height: 7,
    backgroundColor: C.track,
    borderRadius: 5,
    overflow: "hidden",
  },
  fill: { height: "100%", backgroundColor: C.green, borderRadius: 5 },
  bottom: { flexDirection: "row", alignItems: "center", gap: 10 },
  cta: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 9,
    flexShrink: 0,
  },
});
